import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAviatorRoundHistory, getUserAggregatedInfo, getWebGameTemplateHtml, resetWebGameTemplateHtml, updateWebGameTemplateHtml } from '../Api';
import WebGameTemplateHtmlEditorDialog from '../components/WebGameTemplateHtmlEditorDialog';
import AggregateUserInfoDto from '../models/dtos/AggregateUserInfoDto';
import type { AviatorAdminRoundDto, AviatorAdminRoundHistoryResponseDto } from '../models/dtos/WebGameDto';
import type { WebGameTemplateHtmlDto } from '../models/dtos/WebGameDto';

const AVIATOR_GAME_CODE = 'aviator-simplified';
const AVIATOR_HISTORY_POLL_MS = 5000;
const AVIATOR_HISTORY_LIMIT = 25;

/** Shared with webgames preview hub (`preview-index.html`) for base-origin overrides. */
const LS_PREVIEW_BASE_ORIGIN = 'betup.webgames.devPreviewBaseOrigin';
const LS_PREVIEW_FULL_URL = 'betup.webGamePreview.overrideFullUrl';

function readStoredPreviewOverrides(): { baseOrigin: string; fullUrl: string } {
    if (typeof window === 'undefined') {
        return { baseOrigin: '', fullUrl: '' };
    }
    return {
        baseOrigin: window.localStorage.getItem(LS_PREVIEW_BASE_ORIGIN) ?? '',
        fullUrl: window.localStorage.getItem(LS_PREVIEW_FULL_URL) ?? ''
    };
}

function persistPreviewBaseOrigin(value: string) {
    const v = value.trim();
    if (v === '') {
        window.localStorage.removeItem(LS_PREVIEW_BASE_ORIGIN);
    } else {
        window.localStorage.setItem(LS_PREVIEW_BASE_ORIGIN, v);
    }
}

function persistPreviewFullUrl(value: string) {
    const v = value.trim();
    if (v === '') {
        window.localStorage.removeItem(LS_PREVIEW_FULL_URL);
    } else {
        window.localStorage.setItem(LS_PREVIEW_FULL_URL, v);
    }
}

/**
 * When a full URL is set it wins. Otherwise, when base origin is set, only scheme+host+port
 * of the default catalog URL are replaced (path, query, hash preserved).
 */
function resolveWebGamePreviewIframeSrc(
    defaultGameUrl: string,
    baseOriginOverride: string,
    fullUrlOverride: string
): string {
    const full = fullUrlOverride.trim();
    if (full !== '') {
        try {
            return new URL(full).toString();
        } catch {
            return defaultGameUrl;
        }
    }
    const baseRaw = baseOriginOverride.trim();
    if (baseRaw === '' || defaultGameUrl.trim() === '') {
        return defaultGameUrl;
    }
    try {
        const game = new URL(defaultGameUrl);
        const base = new URL(baseRaw);
        return `${base.origin}${game.pathname}${game.search}${game.hash}`;
    } catch {
        return defaultGameUrl;
    }
}

type CallbackMessage = {
    id: number;
    method: string;
    receivedAt: string;
    payload: unknown;
};

const formatTimestamp = (value: string | number | null | undefined): string => {
    if (value == null || value === '') return '-';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(value);
};

const formatMultiplier = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return '-';
    return `${value.toFixed(2)}x`;
};

const formatAmount = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return '-';
    return String(Math.trunc(value));
};

const roundPhaseChipColor = (
    phase: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    const normalized = phase.toUpperCase();
    if (normalized === 'RUNNING') return 'success';
    if (normalized === 'BETTING') return 'primary';
    if (normalized === 'CRASHED') return 'error';
    if (normalized.includes('RUNNING')) return 'success';
    if (normalized.includes('BET')) return 'primary';
    if (normalized.includes('CRASH')) return 'error';
    return 'default';
};

const betOutcomeChipProps = (
    resolved: string
): { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' } => {
    switch (resolved) {
        case 'CASHED_OUT':
            return { label: 'Won', color: 'success' };
        case 'CRASHED':
            return { label: 'Lost', color: 'error' };
        case 'PENDING':
            return { label: 'Pending', color: 'warning' };
        default:
            return { label: resolved, color: 'default' };
    }
};

export default function WebGamePreviewPage() {
    const [searchParams] = useSearchParams();
    const url = searchParams.get('url') ?? '';
    const title = searchParams.get('title') ?? 'Web Game';
    const userId = searchParams.get('userId') ? Number(searchParams.get('userId')) : null;
    const gameCode = searchParams.get('gameCode')?.trim() ?? '';
    const showAviatorHistory = gameCode === AVIATOR_GAME_CODE;

    const [overrideBaseOrigin, setOverrideBaseOrigin] = React.useState(() => readStoredPreviewOverrides().baseOrigin);
    const [overrideFullUrl, setOverrideFullUrl] = React.useState(() => readStoredPreviewOverrides().fullUrl);
    const [previewReloadNonce, setPreviewReloadNonce] = React.useState(0);
    const [templateEditorExpanded, setTemplateEditorExpanded] = React.useState(false);
    const [templateEditorDialogOpen, setTemplateEditorDialogOpen] = React.useState(false);
    const [templateHtml, setTemplateHtml] = React.useState<WebGameTemplateHtmlDto | null>(null);
    const [templateDraft, setTemplateDraft] = React.useState('');
    const [templateLoading, setTemplateLoading] = React.useState(false);
    const [templateSaving, setTemplateSaving] = React.useState(false);
    const [templateMessage, setTemplateMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

    React.useEffect(() => {
        persistPreviewBaseOrigin(overrideBaseOrigin);
    }, [overrideBaseOrigin]);

    React.useEffect(() => {
        persistPreviewFullUrl(overrideFullUrl);
    }, [overrideFullUrl]);

    const iframeSrc = React.useMemo(
        () => resolveWebGamePreviewIframeSrc(url, overrideBaseOrigin, overrideFullUrl),
        [url, overrideBaseOrigin, overrideFullUrl]
    );

    const iframeSrcWithReload = React.useMemo(() => {
        if (!iframeSrc || previewReloadNonce === 0) {
            return iframeSrc;
        }
        try {
            const parsed = new URL(iframeSrc);
            parsed.searchParams.set('_tplReload', String(previewReloadNonce));
            return parsed.toString();
        } catch {
            return iframeSrc;
        }
    }, [iframeSrc, previewReloadNonce]);

    const bumpPreviewReload = React.useCallback(() => {
        setPreviewReloadNonce(prev => prev + 1);
    }, []);

    const loadTemplateHtml = React.useCallback(async () => {
        if (!gameCode) return;
        setTemplateLoading(true);
        setTemplateMessage(null);
        const result = await getWebGameTemplateHtml(gameCode);
        setTemplateLoading(false);
        if (result == null) {
            setTemplateMessage({ type: 'error', text: 'Failed to load template HTML from games server.' });
            return;
        }
        setTemplateHtml(result);
        setTemplateDraft(result.htmlContent);
    }, [gameCode]);

    React.useEffect(() => {
        if ((!templateEditorExpanded && !templateEditorDialogOpen) || !gameCode) return;
        void loadTemplateHtml();
    }, [templateEditorExpanded, templateEditorDialogOpen, gameCode, loadTemplateHtml]);

    const openTemplateEditorDialog = React.useCallback(async () => {
        setTemplateEditorDialogOpen(true);
        if (gameCode) {
            await loadTemplateHtml();
        }
    }, [gameCode, loadTemplateHtml]);

    const closeTemplateEditorDialog = React.useCallback(() => {
        setTemplateEditorDialogOpen(false);
        setTemplateMessage(null);
    }, []);

    const saveTemplateHtml = React.useCallback(async () => {
        if (!gameCode) return;
        setTemplateSaving(true);
        setTemplateMessage(null);
        const result = await updateWebGameTemplateHtml(gameCode, { htmlContent: templateDraft });
        setTemplateSaving(false);
        if (result == null) {
            setTemplateMessage({ type: 'error', text: 'Failed to save template HTML.' });
            return;
        }
        setTemplateHtml(result);
        setTemplateDraft(result.htmlContent);
        setTemplateMessage({ type: 'success', text: 'Template saved to MongoDB. Reloading preview…' });
        bumpPreviewReload();
    }, [gameCode, templateDraft, bumpPreviewReload]);

    const resetTemplateHtml = React.useCallback(async () => {
        if (!gameCode) return;
        setTemplateSaving(true);
        setTemplateMessage(null);
        const result = await resetWebGameTemplateHtml(gameCode);
        setTemplateSaving(false);
        if (result == null) {
            setTemplateMessage({ type: 'error', text: 'Failed to reset template to classpath default.' });
            return;
        }
        setTemplateHtml(result);
        setTemplateDraft(result.htmlContent);
        setTemplateMessage({ type: 'success', text: 'Override removed. Preview uses bundled classpath HTML again.' });
        bumpPreviewReload();
    }, [gameCode, bumpPreviewReload]);

    const fullOverrideInvalid =
        overrideFullUrl.trim() !== '' &&
        (() => {
            try {
                new URL(overrideFullUrl.trim());
                return false;
            } catch {
                return true;
            }
        })();

    const baseOverrideInvalid =
        overrideBaseOrigin.trim() !== '' &&
        overrideFullUrl.trim() === '' &&
        url.trim() !== '' &&
        (() => {
            try {
                new URL(overrideBaseOrigin.trim());
                return false;
            } catch {
                return true;
            }
        })();

    const clearLoadOverrides = React.useCallback(() => {
        setOverrideBaseOrigin('');
        setOverrideFullUrl('');
        window.localStorage.removeItem(LS_PREVIEW_BASE_ORIGIN);
        window.localStorage.removeItem(LS_PREVIEW_FULL_URL);
    }, []);

    const [userInfo, setUserInfo] = React.useState<AggregateUserInfoDto | null>(null);
    const [loadingUser, setLoadingUser] = React.useState(false);
    const [callbackMessages, setCallbackMessages] = React.useState<CallbackMessage[]>([]);
    const [aviatorHistory, setAviatorHistory] = React.useState<AviatorAdminRoundHistoryResponseDto | null>(null);
    const [aviatorLoading, setAviatorLoading] = React.useState(false);
    const [aviatorError, setAviatorError] = React.useState<string | null>(null);

    const refreshUserInfo = React.useCallback(async () => {
        if (userId == null || !Number.isFinite(userId)) return;
        setLoadingUser(true);
        const info = await getUserAggregatedInfo(userId);
        setUserInfo(info);
        setLoadingUser(false);
    }, [userId]);

    React.useEffect(() => {
        refreshUserInfo();
    }, [refreshUserInfo]);

    React.useEffect(() => {
        if (!showAviatorHistory) {
            setAviatorHistory(null);
            setAviatorError(null);
            setAviatorLoading(false);
            return;
        }

        let cancelled = false;
        let hasLoadedOnce = false;

        const loadHistory = async () => {
            if (!hasLoadedOnce) {
                setAviatorLoading(true);
            }
            const result = await getAviatorRoundHistory(0, AVIATOR_HISTORY_LIMIT);
            if (cancelled) return;
            hasLoadedOnce = true;
            setAviatorLoading(false);
            if (result == null) {
                setAviatorError('Failed to load Aviator round history.');
                return;
            }
            setAviatorError(null);
            setAviatorHistory(result);
        };

        void loadHistory();
        const intervalId = window.setInterval(() => {
            void loadHistory();
        }, AVIATOR_HISTORY_POLL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [showAviatorHistory]);

    React.useEffect(() => {
        const listener = (event: MessageEvent) => {
            if (!iframeSrc) return;

            let gameOrigin: string;
            try {
                gameOrigin = new URL(iframeSrc).origin;
            } catch {
                return;
            }
            if (event.origin !== gameOrigin) return;
            if (event.data?.source !== 'BetupWebGame') return;

            const method = String(event.data.method ?? 'unknown');
            setCallbackMessages(prev => [
                {
                    id: Date.now(),
                    method,
                    receivedAt: new Date().toISOString(),
                    payload: event.data.payload
                },
                ...prev
            ].slice(0, 30));

            if (event.data.method === 'onBalanceUpdated') {
                const balanceDelta = Number(event.data.payload?.balanceDelta ?? 0);
                if (Number.isFinite(balanceDelta)) {
                    setUserInfo(prev => prev == null ? prev : {
                        ...prev,
                        balance: {
                            ...prev.balance,
                            moneyInfo: prev.balance.moneyInfo + balanceDelta
                        }
                    });
                }
                refreshUserInfo();
            }

            if (event.data.method === 'onGameResult' || event.data.method === 'onNotEnoughMoney') {
                refreshUserInfo();
            }
        };

        window.addEventListener('message', listener);
        return () => window.removeEventListener('message', listener);
    }, [refreshUserInfo, iframeSrc]);

    const userName = userInfo?.general?.name ?? (userId != null ? `User ${userId}` : 'User');
    const balance = userInfo?.balance?.moneyInfo;

    const renderRoundPhaseChip = (phase: string) => {
        const color = roundPhaseChipColor(phase);
        const variant = color === 'default' ? 'outlined' : 'filled';
        return (
            <Chip size="small" label={phase} color={color} variant={variant} />
        );
    };

    const renderBetOutcomeChip = (resolved: string) => {
        const { label, color } = betOutcomeChipProps(resolved);
        const variant = color === 'default' ? 'outlined' : 'filled';
        return <Chip size="small" label={label} color={color} variant={variant} />;
    };

    const renderAviatorRound = (round: AviatorAdminRoundDto) => {
        const winners = round.bets.filter(b => b.resolvedOutcome === 'CASHED_OUT');
        const houseEdgePct =
            round.houseEdge != null && Number.isFinite(round.houseEdge)
                ? `${(round.houseEdge * 100).toFixed(2)}%`
                : '-';
        return (
            <Box key={round.roundId} sx={{ mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Round #{round.roundNumber}
                    </Typography>
                    {renderRoundPhaseChip(round.phase)}
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: { xs: 0.25, sm: 0 } }}>
                        crash {formatMultiplier(round.crashMultiplier)} · final {formatMultiplier(round.finalMultiplier)}
                        {' · '}
                        closed {formatTimestamp(round.closedAt ?? round.createdAt)}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    In {formatAmount(round.totalBets)} · Out {formatAmount(round.totalPayouts)} · Net (house){' '}
                    {formatAmount(round.netHouseAmount)} · Sessions {round.betSessionCount} · Users{' '}
                    {round.participantCount}
                    {' · '}House edge {houseEdgePct}
                </Typography>
                {winners.length > 0 && (
                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.75 }}>
                        Winners ({winners.length}):{' '}
                        {winners
                            .map(w => `${w.playerName ?? `user ${w.userId}`}: ${formatAmount(w.returnAmount)} @ ${formatTimestamp(w.closedAt ?? w.createdAt)}`)
                            .join(' · ')}
                    </Typography>
                )}
                <Table size="small" sx={{ '& td': { py: 0.5 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Player</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Bet</TableCell>
                            <TableCell>Payout</TableCell>
                            <TableCell>Result</TableCell>
                            <TableCell>Round</TableCell>
                            <TableCell>Meta</TableCell>
                            <TableCell>Placed</TableCell>
                            <TableCell>Settled</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {round.bets.length === 0 ? (
                            <TableRow hover>
                                <TableCell colSpan={9}>
                                    <Typography variant="caption" color="text.secondary">
                                        No wager sessions tied to this round (or IDs did not align).
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            round.bets.map(b => (
                                <TableRow hover key={b.sessionId}>
                                    <TableCell sx={{ maxWidth: 120 }}>{b.playerName ?? '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{b.userId}</TableCell>
                                    <TableCell align="right">{formatAmount(b.betAmount)}</TableCell>
                                    <TableCell align="right">{formatAmount(b.returnAmount)}</TableCell>
                                    <TableCell>{renderBetOutcomeChip(b.resolvedOutcome)}</TableCell>
                                    <TableCell>{renderRoundPhaseChip(b.roundPhase)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="caption">{b.status}</Typography>
                                        {b.outcome != null ? (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {b.outcome}
                                            </Typography>
                                        ) : null}
                                        {b.cashoutMultiplier != null ? (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                @ {formatMultiplier(b.cashoutMultiplier)}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(b.createdAt)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(b.closedAt ?? null)}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3, px: 2 }}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 2,
                    flexDirection: { xs: 'column', lg: 'row' }
                }}
            >
                <Stack spacing={2} alignItems="center" sx={{ width: { xs: '100%', lg: 430 } }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%', maxWidth: 430 }}
                    >
                        <Typography variant="h6">{title}</Typography>
                        {iframeSrc && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                onClick={() => window.open(iframeSrc, '_blank', 'noopener,noreferrer')}
                            >
                                Open
                            </Button>
                        )}
                    </Stack>
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                            width: '100%',
                            maxWidth: 430,
                            bgcolor: '#fff',
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            '&:before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Developer: game load URL
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Overrides apply to this tab only and persist in localStorage. Full URL wins over base
                                    origin. Base origin uses the same path and query as the catalog URL from Play.
                                </Typography>
                                {(fullOverrideInvalid || baseOverrideInvalid) && (
                                    <Alert severity="warning">
                                        {fullOverrideInvalid
                                            ? 'Full URL override must be a valid absolute URL (include http:// or https://).'
                                            : 'Base origin override must be a valid URL (e.g. http://localhost:8080).'}
                                    </Alert>
                                )}
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Full URL override"
                                    placeholder="https://games.example/webgames/templates/plinko.html?..."
                                    value={overrideFullUrl}
                                    onChange={e => setOverrideFullUrl(e.target.value)}
                                    helperText="Optional. Replaces the entire iframe URL (session params must be included if needed)."
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Base origin override"
                                    placeholder="http://localhost:8080"
                                    value={overrideBaseOrigin}
                                    onChange={e => setOverrideBaseOrigin(e.target.value)}
                                    disabled={overrideFullUrl.trim() !== ''}
                                    helperText={
                                        overrideFullUrl.trim() !== ''
                                            ? 'Clear full URL override to use base origin.'
                                            : 'Replaces scheme, host, and port only; path and query stay from the Play URL. Same key as preview-index hub.'
                                    }
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={clearLoadOverrides}
                                    disabled={overrideBaseOrigin === '' && overrideFullUrl === ''}
                                >
                                    Clear overrides
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 430,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: '#fff',
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            px: 2,
                            py: 1.25
                        }}
                    >
                        <Box>
                            <Typography variant="caption" color="text.secondary">Player</Typography>
                            <Typography variant="body1" fontWeight={600}>{userName}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">Balance</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {loadingUser && <CircularProgress size={14} />}
                                <Typography variant="body1" fontWeight={600}>{balance == null ? '-' : balance}</Typography>
                            </Stack>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            width: 'min(100%, 430px)',
                            height: 'min(calc(100vh - 190px), 844px)',
                            minHeight: 560,
                            borderRadius: '28px',
                            p: '12px',
                            bgcolor: '#111827',
                            boxShadow: '0 16px 40px rgba(15, 23, 42, 0.25)'
                        }}
                    >
                        <Box sx={{ width: '100%', height: '100%', borderRadius: '20px', overflow: 'hidden', bgcolor: '#fff' }}>
                            {iframeSrcWithReload ? (
                                <iframe
                                    title={title}
                                    key={iframeSrcWithReload}
                                    src={iframeSrcWithReload}
                                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                    sandbox="allow-scripts allow-forms allow-same-origin"
                                />
                            ) : (
                                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                                    <Typography color="text.secondary">Missing game preview URL.</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Stack>
                <Stack
                    spacing={2}
                    sx={{
                        width: { xs: '100%', lg: 420 },
                        maxHeight: { lg: 'calc(100vh - 48px)' },
                        overflow: 'auto'
                    }}
                >
                    {gameCode && (
                        <Accordion
                            disableGutters
                            elevation={0}
                            expanded={templateEditorExpanded}
                            onChange={(_, expanded) => setTemplateEditorExpanded(expanded)}
                            sx={{
                                bgcolor: '#fff',
                                border: theme => `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px',
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="subtitle1" fontWeight={700}>Edit template HTML</Typography>
                                    {templateHtml && (
                                        <Chip
                                            size="small"
                                            label={templateHtml.source === 'override' ? 'Mongo override' : 'Classpath default'}
                                            color={templateHtml.source === 'override' ? 'warning' : 'default'}
                                        />
                                    )}
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={1.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        Saves HTML to MongoDB on the games server. Play routes, dev preview routes, and
                                        Android WebView load the override immediately — no games-server redeploy. Clear
                                        overrides to fall back to the bundled classpath file.
                                    </Typography>
                                    {templateHtml && (
                                        <Typography variant="caption" color="text.secondary">
                                            Template file: <strong>{templateHtml.templateName}</strong>
                                            {templateHtml.updatedAt ? ` · updated ${formatTimestamp(templateHtml.updatedAt)}` : ''}
                                        </Typography>
                                    )}
                                    {templateMessage && (
                                        <Alert severity={templateMessage.type}>{templateMessage.text}</Alert>
                                    )}
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        sx={{ py: 1, fontWeight: 700 }}
                                        onClick={() => void openTemplateEditorDialog()}
                                        disabled={!gameCode || templateLoading || templateSaving}
                                    >
                                        Deschide editor mare (Monaco) / Full-screen code editor
                                    </Button>
                                    <TextField
                                        multiline
                                        minRows={14}
                                        maxRows={24}
                                        fullWidth
                                        value={templateDraft}
                                        onChange={event => setTemplateDraft(event.target.value)}
                                        disabled={templateLoading || templateSaving}
                                        placeholder="Loading template HTML…"
                                        InputProps={{
                                            sx: {
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                fontSize: 12,
                                                lineHeight: 1.45
                                            }
                                        }}
                                    />
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => void openTemplateEditorDialog()}
                                            disabled={!gameCode || templateLoading || templateSaving}
                                        >
                                            Editor mare (Monaco)
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => void saveTemplateHtml()}
                                            disabled={templateLoading || templateSaving || !templateDraft.trim()}
                                        >
                                            {templateSaving ? 'Saving…' : 'Save HTML'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => void loadTemplateHtml()}
                                            disabled={templateLoading || templateSaving}
                                        >
                                            Reload
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => void resetTemplateHtml()}
                                            disabled={templateLoading || templateSaving || templateHtml?.source !== 'override'}
                                        >
                                            Reset to classpath
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={bumpPreviewReload}
                                            disabled={!iframeSrc}
                                        >
                                            Reload preview
                                        </Button>
                                        {templateLoading && <CircularProgress size={18} />}
                                    </Stack>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Box
                        sx={{
                            bgcolor: '#fff',
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            p: 2
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={700}>Callback log</Typography>
                            <Button size="small" onClick={() => setCallbackMessages([])} disabled={callbackMessages.length === 0}>
                                Clear
                            </Button>
                        </Stack>
                        {callbackMessages.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No callbacks received yet.</Typography>
                        ) : (
                            <Stack spacing={1.25}>
                                {callbackMessages.map(message => (
                                    <Box
                                        key={`${message.id}-${message.method}`}
                                        sx={{
                                            border: theme => `1px solid ${theme.palette.divider}`,
                                            borderRadius: '8px',
                                            p: 1.25,
                                            bgcolor: '#f8fafc'
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Typography variant="body2" fontWeight={700}>{message.method}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(message.receivedAt).toLocaleTimeString()}
                                            </Typography>
                                        </Stack>
                                        <Box
                                            component="pre"
                                            sx={{
                                                mt: 1,
                                                mb: 0,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                fontFamily: 'monospace'
                                            }}
                                        >
                                            {JSON.stringify(message.payload, null, 2)}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    {showAviatorHistory && (
                        <Box
                            sx={{
                                bgcolor: '#fff',
                                border: theme => `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px',
                                p: 2
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle1" fontWeight={700}>Aviator round history</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {aviatorLoading && <CircularProgress size={16} />}
                                    <Typography variant="caption" color="text.secondary">
                                        Refreshes every {AVIATOR_HISTORY_POLL_MS / 1000}s
                                    </Typography>
                                </Stack>
                            </Stack>
                            {aviatorError && (
                                <Alert severity="error" sx={{ mb: 1.5 }}>{aviatorError}</Alert>
                            )}
                            {aviatorHistory == null && aviatorError == null && aviatorLoading ? (
                                <Typography variant="body2" color="text.secondary">Loading rounds...</Typography>
                            ) : aviatorHistory != null && aviatorHistory.items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No rounds returned yet.</Typography>
                            ) : aviatorHistory != null ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Showing {aviatorHistory.items.length} of {aviatorHistory.totalItems} rounds · bet rows
                                        use resolved results (pending bets show lost once the stored round phase is CRASHED).
                                    </Typography>
                                    {aviatorHistory.items.map(renderAviatorRound)}
                                </>
                            ) : null}
                        </Box>
                    )}
                </Stack>
            </Box>
            <WebGameTemplateHtmlEditorDialog
                open={templateEditorDialogOpen}
                onClose={closeTemplateEditorDialog}
                value={templateDraft}
                onChange={setTemplateDraft}
                gameCode={gameCode}
                templateFileName={templateHtml?.templateName}
                source={templateHtml?.source === 'override' || templateHtml?.source === 'classpath' ? templateHtml.source : undefined}
                updatedAtLabel={templateHtml?.updatedAt ? `Updated ${formatTimestamp(templateHtml.updatedAt)}` : undefined}
                loading={templateLoading}
                saving={templateSaving}
                message={templateMessage}
                canReset={templateHtml?.source === 'override'}
                onSave={() => void saveTemplateHtml()}
                onReloadFromServer={() => void loadTemplateHtml()}
                onResetToClasspath={() => void resetTemplateHtml()}
                onReloadPreview={bumpPreviewReload}
            />
        </Box>
    );
}
